<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  resource: { type: Object, required: true },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'edit', 'delete']);
const { t } = useI18n();
const [showMenu, toggleMenu] = useToggle();

const KIND_ICONS = {
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  audio: 'i-lucide-volume-2',
  file: 'i-lucide-file-text',
};

const kindIcon = computed(
  () => KIND_ICONS[props.resource.kind] || KIND_ICONS.file
);
const kindLabel = computed(() => {
  const labels = {
    image: t('ARROWS_AI.RESOURCES.KINDS.IMAGE'),
    video: t('ARROWS_AI.RESOURCES.KINDS.VIDEO'),
    audio: t('ARROWS_AI.RESOURCES.KINDS.AUDIO'),
    file: t('ARROWS_AI.RESOURCES.KINDS.FILE'),
  };
  return labels[props.resource.kind] || labels.file;
});
const showPreview = computed(
  () => props.resource.kind === 'image' && Boolean(props.resource.fileUrl)
);

const formatSize = size => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const menuItems = computed(() => [
  {
    label: t('ARROWS_AI.RESOURCES.EDIT'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('ARROWS_AI.RESOURCES.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const handleAction = ({ action }) => {
  toggleMenu(false);
  if (action === 'edit') emit('edit', props.resource);
  if (action === 'delete') emit('delete', props.resource.id);
};
</script>

<template>
  <CardLayout selectable class="relative">
    <div class="absolute top-7 ltr:left-3 rtl:right-3">
      <Checkbox
        :model-value="selected"
        @update:model-value="emit('select', resource.id)"
      />
    </div>
    <div class="flex items-start justify-between w-full gap-3">
      <div class="flex items-start min-w-0 gap-3">
        <img
          v-if="showPreview"
          :src="resource.fileUrl"
          :alt="resource.name"
          class="object-cover rounded-lg size-14 shrink-0 bg-n-alpha-2"
        />
        <span
          v-else
          class="grid rounded-lg size-14 shrink-0 place-content-center bg-n-alpha-2 text-n-slate-11"
        >
          <span :class="kindIcon" class="size-5" />
        </span>
        <div class="min-w-0">
          <h3 class="text-base truncate text-n-slate-12">
            {{ resource.name }}
          </h3>
          <p class="mt-1 text-sm truncate text-n-slate-11">
            {{
              t('ARROWS_AI.RESOURCES.META', {
                kind: kindLabel,
                fileName: resource.fileName,
                size: formatSize(resource.size),
              })
            }}
          </p>
          <p
            v-if="resource.description"
            class="mt-1 text-sm text-n-slate-11 line-clamp-2"
          >
            {{ resource.description }}
          </p>
        </div>
      </div>
      <div class="relative shrink-0">
        <Button
          icon="i-lucide-ellipsis-vertical"
          color="slate"
          size="xs"
          @click="toggleMenu()"
        />
        <DropdownMenu
          v-if="showMenu"
          :menu-items="menuItems"
          class="mt-1 ltr:right-0 top-full"
          @action="handleAction"
        />
      </div>
    </div>
  </CardLayout>
</template>
